import { Injectable } from "@angular/core";
import { AuthService } from "@auth0/auth0-angular";
import { BehaviorSubject, forkJoin, interval, Observable, of, Subscription } from "rxjs";
import { catchError, finalize, map, switchMap, take, tap, shareReplay } from "rxjs/operators";
import { ClientNotification } from "../models/clientNotification";
import { Event } from "../models/event";
import { Player } from "../models/player";
import { PendingRatingReview } from "../models/ratingReview";
import { EventService } from "./event.service";
import { PlayerService } from "./player.service";
import { nogoshIconClass } from "../icons/nogosh-icons.generated";

@Injectable({
  providedIn: "root"
})
export class ClientNotificationService {
  private static readonly AUTO_REFRESH_INTERVAL_MS = 60000;
  private readonly dismissedStorageKey = "dismissedClientNotifications";
  private readonly readStorageKey = "readClientNotifications";
  private readonly eventSnapshotsStorageKey = "clientNotificationEventSnapshots";
  private readonly notificationCreatedAtStorageKey = "clientNotificationCreatedAt";
  private readonly notificationsSubject = new BehaviorSubject<ClientNotification[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private autoRefreshSubscription: Subscription;

  public readonly notifications$ = this.notificationsSubject.asObservable();
  public readonly loading$ = this.loadingSubject.asObservable();
  public readonly count$ = this.notifications$.pipe(
    map((notifications) => notifications.filter((notification) => !notification.read).length)
  );

  constructor(
    private playerService: PlayerService,
    private eventService: EventService,
    private auth: AuthService
  ) {}

  private pendingPlayerId: string;
  private pendingPreparation: Observable<ClientNotification[]>;

  public prepare(): Observable<ClientNotification[]> {
    const playerId = this.playerService.getCurrentPlayer()?.id;
    if (this.pendingPreparation && this.pendingPlayerId === playerId) return this.pendingPreparation;
    if (this.pendingPlayerId !== playerId) this.notificationsSubject.next([]);
    this.pendingPlayerId = playerId;
    this.loadingSubject.next(true);
    this.pendingPreparation = this.loadNotifications().pipe(
      tap(notifications => {
        if (this.playerService.getCurrentPlayer()?.id === playerId) this.notificationsSubject.next(notifications);
      }),
      finalize(() => { if (this.pendingPlayerId === playerId) { this.pendingPreparation = null; this.loadingSubject.next(false); } }),
      shareReplay({bufferSize: 1, refCount: true})
    );
    return this.pendingPreparation;
  }

  public refresh(): void {
    this.prepare().subscribe({error: () => {}});
  }

  public startAutoRefresh(): void {
    if (this.autoRefreshSubscription) {
      return;
    }

    this.refresh();
    this.autoRefreshSubscription = interval(ClientNotificationService.AUTO_REFRESH_INTERVAL_MS).subscribe(() => this.refresh());
  }

  public stopAutoRefresh(): void {
    if (!this.autoRefreshSubscription) {
      return;
    }

    this.autoRefreshSubscription.unsubscribe();
    this.autoRefreshSubscription = null;
  }

  public dismiss(notificationId: string): void {
    if (!notificationId) {
      return;
    }

    this.notificationsSubject.next(this.notificationsSubject.value.filter((notification) => notification.id !== notificationId));
    this.playerService.dismissClientNotification(notificationId).subscribe(
      () => {},
      () => {}
    );
  }

  public dismissAll(notificationIds: string[]): void {
    const ids = (notificationIds || []).filter((notificationId) => !!notificationId);
    if (ids.length === 0) {
      return;
    }

    const dismissedIds = {};
    ids.forEach((notificationId) => dismissedIds[notificationId] = true);
    this.notificationsSubject.next(this.notificationsSubject.value.filter((notification) => !dismissedIds[notification.id]));
    this.playerService.dismissClientNotifications(ids).subscribe(
      () => {},
      () => {}
    );
  }

  public markRead(notificationIds: string[]): void {
    const ids = (notificationIds || []).filter((notificationId) => !!notificationId);
    if (ids.length === 0) {
      return;
    }

    const readIds = {};
    ids.forEach((notificationId) => readIds[notificationId] = true);
    this.applyReadState(readIds);
    this.playerService.markClientNotificationsRead(ids).subscribe(
      () => {},
      () => {}
    );
  }

  private loadNotifications(): Observable<ClientNotification[]> {
    return this.playerService.getClientNotifications().pipe(
      map((notifications) => notifications || [])
    );
  }

  private buildNotifications(
    player: Player,
    authProfile: any,
    ratingReviews: PendingRatingReview[],
    myEvents: Event[],
    missingResultEvent: Event
  ): ClientNotification[] {
    const notifications: ClientNotification[] = [];
    const authEmailVerified = authProfile && authProfile.email_verified !== undefined ? authProfile.email_verified : null;
    const activeEvents = (myEvents || []).filter((event) => this.isActiveEvent(event));

    if (player && !this.isPlayerEmailVerified(player) && (this.isPlayerEmailUnverified(player) || authEmailVerified === false)) {
      notifications.push({
        id: "email-verification",
        type: "emailVerification",
        titleKey: "notifications.emailVerificationTitle",
        descriptionKey: "notifications.emailVerificationDescription",
        actionKey: "notifications.sendVerificationEmail",
        iconClass: nogoshIconClass("notifications.type.emailVerification"),
        priority: 100
      });
    }

    if (missingResultEvent) {
      notifications.push({
        id: `missing-result-${missingResultEvent.id}`,
        type: "missingResult",
        titleKey: "notifications.missingResultTitle",
        descriptionKey: "notifications.missingResultDescription",
        actionKey: "events.enterResult",
        iconClass: nogoshIconClass("notifications.type.missingResult"),
        priority: 90,
        event: missingResultEvent
      });
    }

    (ratingReviews || []).forEach((ratingReview) => {
      notifications.push({
        id: `rating-review-${ratingReview.id}`,
        type: "ratingReview",
        titleKey: "notifications.ratingReviewTitle",
        descriptionKey: "notifications.ratingReviewDescription",
        actionKey: "notifications.startRatingReview",
        iconClass: nogoshIconClass("notifications.type.ratingReview"),
        priority: 80,
        ratingReview
      });
    });

    notifications.push(...this.buildEventChangeNotifications(activeEvents));
    notifications.push(...this.buildEventNotifications(player, activeEvents));
    this.storeNewEventSnapshots(activeEvents);

    const dismissedIds = this.getDismissedIds(player);
    const readIds = this.getReadIds(player);
    const createdAtById = this.getNotificationCreatedAtById();
    const currentNotificationIds = {};
    let createdAtChanged = false;
    const mappedNotifications = notifications
      .filter((notification) => !dismissedIds[notification.id])
      .map((notification) => Object.assign({}, notification, {
        createdAt: this.getOrCreateNotificationCreatedAt(notification, createdAtById, () => createdAtChanged = true),
        read: !!readIds[notification.id]
      }))
      .sort((first, second) => this.notificationSortTime(second) - this.notificationSortTime(first) || second.priority - first.priority);

    mappedNotifications.forEach((notification) => currentNotificationIds[notification.id] = true);
    if (this.pruneNotificationCreatedAt(createdAtById, currentNotificationIds) || createdAtChanged) {
      localStorage.setItem(this.notificationCreatedAtStorageKey, JSON.stringify(createdAtById));
    }

    return mappedNotifications;
  }

  private buildEventNotifications(player: Player, activeEvents: Event[]): ClientNotification[] {
    if (!player) {
      return [];
    }

    const notifications: ClientNotification[] = [];
    const managerEvents = (activeEvents || []).filter((event) => this.isManager(player, event));
    const playerEvents = (activeEvents || []).filter((event) => !this.isManager(player, event));

    playerEvents.forEach((event) => {
      if (this.hasEnabledRulebook(event)) {
        notifications.push({
          id: `rulebook-reminder-${event.id}`,
          type: "rulebookReminder",
          titleKey: "notifications.rulebookReminderTitle",
          descriptionKey: "notifications.rulebookReminderDescription",
          descriptionParams: { match: event.name || "" },
          actionKey: "notifications.readRules",
          iconClass: nogoshIconClass("notifications.type.rulebookReminder"),
          priority: 76,
          event
        });
      }

      if (this.currentPlayerInviteStatus(event) === "NOT_ANSWERED") {
        if (this.maxPlayers(event) > 0 && this.confirmedCount(event) >= this.maxPlayers(event)) {
          notifications.push({
            id: `match-full-${event.id}`,
            type: "matchFull",
            titleKey: "notifications.matchFullTitle",
            descriptionKey: "notifications.matchFullDescription",
            descriptionParams: { match: event.name || "" },
            actionKey: "notifications.openMatch",
            iconClass: nogoshIconClass("notifications.type.matchFull"),
            priority: 58,
            event
          });
        } else {
          const soon = this.hoursUntil(event) <= 24;
          const participationToken = this.notificationValueToken(this.currentPlayerParticipationModifiedAt(event));
          notifications.push({
            id: `${soon ? "match-response-reminder" : "match-invite"}-${event.id}-${participationToken}`,
            type: soon ? "matchResponseReminder" : "matchInvite",
            titleKey: soon ? "notifications.matchResponseReminderTitle" : "notifications.matchInviteTitle",
            descriptionKey: soon ? "notifications.matchResponseReminderDescription" : "notifications.matchInviteDescription",
            descriptionParams: { match: event.name || "" },
            actionKey: "notifications.openMatch",
            iconClass: nogoshIconClass(soon ? "notifications.type.matchResponseReminder" : "notifications.type.matchInvite"),
            priority: soon ? 88 : 78,
            event
          });
        }
      }
    });

    managerEvents.forEach((event) => {
      const candidateCount = this.countPlayerEventInfos(event, null, "CANDIDATE");
      if (candidateCount > 0) {
        notifications.push({
          id: `candidate-request-${event.id}-${candidateCount}`,
          type: "candidateRequest",
          titleKey: "notifications.candidateRequestTitle",
          descriptionKey: "notifications.candidateRequestDescription",
          descriptionParams: { count: candidateCount, match: event.name || "" },
          actionKey: "notifications.managePlayers",
          iconClass: nogoshIconClass("notifications.type.candidateRequest"),
          priority: 74,
          event
        });
      }

      const notNotifiedCount = this.countPlayerEventInfos(event, "NOT_NOTIFIED", null);
      if (notNotifiedCount > 0) {
        notifications.push({
          id: `pending-invites-${event.id}-${notNotifiedCount}`,
          type: "pendingInvites",
          titleKey: "notifications.pendingInvitesTitle",
          descriptionKey: "notifications.pendingInvitesDescription",
          descriptionParams: { count: notNotifiedCount, match: event.name || "" },
          actionKey: "notifications.openMatch",
          iconClass: nogoshIconClass("notifications.type.pendingInvites"),
          priority: 64,
          event
        });
      }

      if (this.hoursUntil(event) <= 48 && this.confirmedCount(event) < this.maxPlayers(event)) {
        notifications.push({
          id: `low-player-count-${event.id}-${this.confirmedCount(event)}`,
          type: "lowPlayerCount",
          titleKey: "notifications.lowPlayerCountTitle",
          descriptionKey: "notifications.lowPlayerCountDescription",
          descriptionParams: { count: this.confirmedCount(event), max: this.maxPlayers(event), match: event.name || "" },
          actionKey: "notifications.openMatch",
          iconClass: nogoshIconClass("notifications.type.lowPlayerCount"),
          priority: 62,
          event
        });
      }

      if (this.shouldRecommendSos(event)) {
        notifications.push({
          id: `sos-recommended-${event.id}`,
          type: "sosRecommended",
          titleKey: "notifications.sosRecommendedTitle",
          descriptionKey: "notifications.sosRecommendedDescription",
          descriptionParams: { match: event.name || "" },
          actionKey: "notifications.openMatch",
          iconClass: nogoshIconClass("notifications.type.sosRecommended"),
          priority: 60,
          event
        });
      }

      const publicRsvpCount = this.countPublicInviteParticipants(event);
      if (publicRsvpCount > 0) {
        notifications.push({
          id: `public-rsvp-${event.id}-${publicRsvpCount}`,
          type: "publicRsvp",
          titleKey: "notifications.publicRsvpTitle",
          descriptionKey: "notifications.publicRsvpDescription",
          descriptionParams: { count: publicRsvpCount, match: event.name || "" },
          actionKey: "notifications.openMatch",
          iconClass: nogoshIconClass("notifications.type.publicRsvp"),
          priority: 56,
          event
        });
      }
    });

    if (!this.hasLocation(player)) {
      notifications.push({
        id: "profile-location-missing",
        type: "profileLocationMissing",
        titleKey: "notifications.profileLocationMissingTitle",
        descriptionKey: "notifications.profileLocationMissingDescription",
        actionKey: "notifications.openProfile",
        iconClass: nogoshIconClass("notifications.type.profileLocationMissing"),
        priority: 52
      });
    }

    if (activeEvents.length === 0) {
      notifications.push({
        id: "no-active-matches",
        type: "noActiveMatches",
        titleKey: "notifications.noActiveMatchesTitle",
        descriptionKey: "notifications.noActiveMatchesDescription",
        actionKey: "notifications.openMatches",
        iconClass: nogoshIconClass("notifications.type.noActiveMatches"),
        priority: 40
      });
    }

    return notifications;
  }

  private buildEventChangeNotifications(activeEvents: Event[]): ClientNotification[] {
    const previousSnapshots = this.getEventSnapshots();
    const currentEventIds = {};
    const notifications: ClientNotification[] = [];

    (activeEvents || []).forEach((event) => {
      if (!event || !event.id) {
        return;
      }

      currentEventIds[event.id] = true;
      const snapshot = previousSnapshots[event.id];
      if (!snapshot) {
        return;
      }

      const currentTime = this.eventSnapshotTime(event);
      if (snapshot.time && currentTime && snapshot.time !== currentTime) {
        notifications.push({
          id: `match-time-changed-${event.id}-${this.notificationValueToken(currentTime)}`,
          type: "matchTimeChanged",
          titleKey: "notifications.matchTimeChangedTitle",
          descriptionKey: "notifications.matchTimeChangedDescription",
          descriptionParams: { match: event.name || snapshot.name || "" },
          actionKey: "notifications.openMatch",
          iconClass: nogoshIconClass("notifications.type.matchTimeChanged"),
          priority: 86,
          event
        });
      }

      const currentLocation = this.eventSnapshotLocation(event);
      if (snapshot.location && currentLocation && snapshot.location !== currentLocation) {
        notifications.push({
          id: `match-location-changed-${event.id}-${this.notificationValueToken(currentLocation)}`,
          type: "matchLocationChanged",
          titleKey: "notifications.matchLocationChangedTitle",
          descriptionKey: "notifications.matchLocationChangedDescription",
          descriptionParams: { match: event.name || snapshot.name || "" },
          actionKey: "notifications.openMatch",
          iconClass: nogoshIconClass("notifications.type.matchLocationChanged"),
          priority: 84,
          event
        });
      }
    });

    Object.keys(previousSnapshots).forEach((eventId) => {
      if (!currentEventIds[eventId]) {
        const snapshot = previousSnapshots[eventId];
        if (!this.shouldNotifyMissingSnapshot(snapshot)) {
          return;
        }
        notifications.push({
          id: `match-canceled-${eventId}`,
          type: "matchCanceled",
          titleKey: "notifications.matchCanceledTitle",
          descriptionKey: "notifications.matchCanceledDescription",
          descriptionParams: { match: snapshot.name || "" },
          actionKey: "notifications.openMatches",
          iconClass: nogoshIconClass("notifications.type.matchCanceled"),
          priority: 82
        });
      }
    });

    return notifications;
  }

  private getDismissedIds(player: Player): {[notificationId: string]: boolean} {
    const dismissedIds = this.getLocalDismissedIds();
    ((player && player.dismissedClientNotificationIds) || []).forEach((notificationId) => {
      dismissedIds[notificationId] = true;
    });
    return dismissedIds;
  }

  private getLocalDismissedIds(): {[notificationId: string]: boolean} {
    try {
      const dismissedIds = JSON.parse(localStorage.getItem(this.dismissedStorageKey) || "[]");
      return (Array.isArray(dismissedIds) ? dismissedIds : []).reduce((result, notificationId) => {
        result[notificationId] = true;
        return result;
      }, {});
    } catch {
      return {};
    }
  }

  private getReadIds(player: Player): {[notificationId: string]: boolean} {
    const readIds = this.getLocalReadIds();
    ((player && player.readClientNotificationIds) || []).forEach((notificationId) => {
      readIds[notificationId] = true;
    });
    return readIds;
  }

  private getLocalReadIds(): {[notificationId: string]: boolean} {
    try {
      const readIds = JSON.parse(localStorage.getItem(this.readStorageKey) || "[]");
      return (Array.isArray(readIds) ? readIds : []).reduce((result, notificationId) => {
        result[notificationId] = true;
        return result;
      }, {});
    } catch {
      return {};
    }
  }

  private getNotificationCreatedAtById(): {[notificationId: string]: string} {
    try {
      const createdAtById = JSON.parse(localStorage.getItem(this.notificationCreatedAtStorageKey) || "{}");
      return createdAtById && typeof createdAtById === "object" ? createdAtById : {};
    } catch {
      return {};
    }
  }

  private getOrCreateNotificationCreatedAt(
    notification: ClientNotification,
    createdAtById: {[notificationId: string]: string},
    markChanged: () => void
  ): string {
    if (!notification || !notification.id) {
      return new Date().toISOString();
    }

    if (!createdAtById[notification.id]) {
      createdAtById[notification.id] = new Date().toISOString();
      markChanged();
    }

    return createdAtById[notification.id];
  }

  private notificationSortTime(notification: ClientNotification): number {
    if (!notification || !notification.createdAt) {
      return 0;
    }

    const time = new Date(notification.createdAt).getTime();
    return isNaN(time) ? 0 : time;
  }

  private pruneNotificationCreatedAt(createdAtById: {[notificationId: string]: string}, currentNotificationIds: {[notificationId: string]: boolean}): boolean {
    let changed = false;
    Object.keys(createdAtById).forEach((notificationId) => {
      if (!currentNotificationIds[notificationId]) {
        delete createdAtById[notificationId];
        changed = true;
      }
    });
    return changed;
  }

  private getEventSnapshots(): {[eventId: string]: {name: string; time: string; location: string}} {
    try {
      const snapshots = JSON.parse(localStorage.getItem(this.eventSnapshotsStorageKey) || "{}");
      return snapshots && typeof snapshots === "object" ? snapshots : {};
    } catch {
      return {};
    }
  }

  private storeNewEventSnapshots(events: Event[]): void {
    const snapshots = this.getEventSnapshots();
    const currentEventIds = {};
    let changed = false;
    (events || []).forEach((event) => {
      if (!event || !event.id || snapshots[event.id]) {
        return;
      }
      currentEventIds[event.id] = true;
      snapshots[event.id] = {
        name: event.name || "",
        time: this.eventSnapshotTime(event),
        location: this.eventSnapshotLocation(event)
      };
      changed = true;
    });
    (events || []).forEach((event) => {
      if (event && event.id) {
        currentEventIds[event.id] = true;
      }
    });
    Object.keys(snapshots).forEach((eventId) => {
      if (!currentEventIds[eventId] && !this.shouldNotifyMissingSnapshot(snapshots[eventId])) {
        delete snapshots[eventId];
        changed = true;
      }
    });
    if (changed) {
      localStorage.setItem(this.eventSnapshotsStorageKey, JSON.stringify(snapshots));
    }
  }

  private shouldNotifyMissingSnapshot(snapshot: {name: string; time: string; location: string}): boolean {
    if (!snapshot || !snapshot.time) {
      return true;
    }

    const snapshotTime = new Date(snapshot.time).getTime();
    if (!Number.isFinite(snapshotTime)) {
      return true;
    }

    return snapshotTime > Date.now();
  }

  private applyReadState(readIds: {[notificationId: string]: boolean}): void {
    this.notificationsSubject.next(this.notificationsSubject.value.map((notification) =>
      Object.assign({}, notification, { read: notification.read || !!readIds[notification.id] })
    ));
  }

  private findMissingResultEvent(player: Player, events: Event[]): Observable<Event> {
    if (!player) {
      return of(null);
    }

    const managerEvents = (events || []).filter((event) =>
      this.isActiveEvent(event) &&
      event.manager &&
      event.manager.id === player.id
    );

    if (managerEvents.length === 0) {
      return of(null);
    }

    return forkJoin(managerEvents.map((event) =>
      this.eventService.fetchHistory(event.originalEventId ? event.originalEventId : event.id).pipe(catchError(() => of([])))
    )).pipe(
      map((historyEventLists: Event[][]) => {
        const latestEventsWithoutResult = (historyEventLists || [])
          .map((historyEvents) => this.latestArchivedManagerEvent(player, historyEvents))
          .filter((event) => event && !this.hasResult(event));

        latestEventsWithoutResult.sort((firstEvent, secondEvent) =>
          new Date(secondEvent.nextOccurrenceTimestamp).getTime() - new Date(firstEvent.nextOccurrenceTimestamp).getTime()
        );

        return latestEventsWithoutResult.length > 0 ? latestEventsWithoutResult[0] : null;
      })
    );
  }

  private latestArchivedManagerEvent(player: Player, events: Event[]): Event {
    const archivedManagerEvents = (events || []).filter((event) =>
      event &&
      event.active === false &&
      event.manager &&
      event.manager.id === player.id
    );

    archivedManagerEvents.sort((firstEvent, secondEvent) =>
      new Date(secondEvent.nextOccurrenceTimestamp).getTime() - new Date(firstEvent.nextOccurrenceTimestamp).getTime()
    );

    return archivedManagerEvents.length > 0 ? archivedManagerEvents[0] : null;
  }

  private hasResult(event: Event): boolean {
    return event && event.whiteScore !== null && event.whiteScore !== undefined &&
      event.blackScore !== null && event.blackScore !== undefined;
  }

  private isActiveEvent(event: Event): boolean {
    return event && event.active !== false;
  }

  private isPlayerEmailVerified(player: Player): boolean {
    return !!player && (player.emailVerified === true || player.email_verified === true);
  }

  private isPlayerEmailUnverified(player: Player): boolean {
    return !!player && (player.emailVerified === false || player.email_verified === false);
  }

  private isManager(player: Player, event: Event): boolean {
    return !!player && !!event && !!event.manager && event.manager.id === player.id;
  }

  private confirmedCount(event: Event): number {
    if (!event) {
      return 0;
    }
    if (event.confirmedCount !== undefined && event.confirmedCount !== null) {
      return Number(event.confirmedCount) || 0;
    }
    if (event.attendance && event.attendance.confirmed !== undefined) {
      return Number(event.attendance.confirmed) || 0;
    }
    return this.countPlayerEventInfos(event, "CONFIRMED", null);
  }

  private maxPlayers(event: Event): number {
    if (!event) {
      return 0;
    }
    if (event.maxPlayers !== undefined && event.maxPlayers !== null) {
      return Number(event.maxPlayers) || 0;
    }
    return event.attendance && event.attendance.max !== undefined ? Number(event.attendance.max) || 0 : 0;
  }

  private countPlayerEventInfos(event: Event, status: string, teamMemberType: string): number {
    return ((event && event.playerEventInfos) || []).filter((playerEventInfo) =>
      (!status || playerEventInfo.status === status) &&
      (!teamMemberType || playerEventInfo.teamMemberType === teamMemberType)
    ).length;
  }

  private countPublicInviteParticipants(event: Event): number {
    return ((event && event.participants) || []).filter((participant: any) =>
      participant && participant.participation && participant.participation.source === "INVITE"
    ).length;
  }

  private hasEnabledRulebook(event: Event): boolean {
    return !!event && !!event.rulebook && event.rulebook.trim().length > 0;
  }

  private shouldRecommendSos(event: Event): boolean {
    const maxPlayers = this.maxPlayers(event);
    if (!event || event.sosUsed || maxPlayers <= 0 || this.hoursUntil(event) > 48) {
      return false;
    }
    return this.confirmedCount(event) < Math.ceil(maxPlayers * 0.7);
  }

  private hoursUntil(event: Event): number {
    const timestamp = this.eventTime(event);
    if (!timestamp) {
      return Number.POSITIVE_INFINITY;
    }
    return (timestamp - Date.now()) / (1000 * 60 * 60);
  }

  private eventTime(event: Event): number {
    if (!event) {
      return 0;
    }
    const value = event.nextOccurrenceTimestamp || event.nextOccurrence as any;
    return value ? new Date(value).getTime() : 0;
  }

  private currentPlayerInviteStatus(event: Event): string {
    if (!event) {
      return null;
    }
    return event.currentPlayerInviteStatus || (event.participation && event.participation.status);
  }

  private currentPlayerParticipationModifiedAt(event: Event): string {
    if (!event || !event.participation || !event.participation.modificationDate) {
      return "created";
    }

    const time = new Date(event.participation.modificationDate).getTime();
    return isNaN(time) ? "created" : new Date(time).toISOString();
  }

  private eventSnapshotTime(event: Event): string {
    const value = event && (event.nextOccurrenceTimestamp || event.nextOccurrence as any);
    return value ? new Date(value).toISOString() : "";
  }

  private eventSnapshotLocation(event: Event): string {
    if (!event) {
      return "";
    }
    return event.locationName || (event.playSpace && event.playSpace.name) || "";
  }

  private notificationValueToken(value: string): string {
    return (value || "").replace(/[^a-zA-Z0-9]/g, "").substring(0, 32);
  }

  private hasLocation(player: Player): boolean {
    return !!player && (!!player.city || !!player.address || !!player.locationInfo);
  }

}
